import { IAuthorizationDto } from "./authorization.dto";
import { IScreenInput, IScreenOutput, ISymbol } from "./builder.dtos";
import { IDesign } from "./design";
import { IDevices } from "./devices";
import { IDisplayItem } from "./display-item";
import { EScreenType, ESubScreenType } from "./screen-type";

export interface IScreenDesign extends IDesign {
  projectId: string;
  devices: IDevices;
  authorization: IAuthorizationDto;
  isAbsoluteDesign?: boolean;
  languages: ILanguage[];
  onClickActionComponentId?: string;
  shortcuts?: { [key: string]: string };
  /**
   * The screen's OWN metadata, parsed from the `publicJSON` column that the single-screen
   * GET already carries (KOA-18173 follow-up). Authoritative for the open screen: it lands
   * with the tree in the same response and the same synchronous redux dispatch, so it
   * cannot disagree with `sdId`, and it is produced by an entity-materializing query — i.e.
   * branch-overlay correct, unlike the project screen list (a column projection).
   *
   * Never serialized into the `JSON` column: the save strips it before stringify so the tree
   * bytes — and therefore the dirty-hash and the server's IsScreenChanged comparison — are
   * unaffected.
   */
  publicMetaData?: IScreenDesignPublicJSON;
}

export interface IScreenDesignDictionary {
  [key: number]: IDisplayItem;
}

export enum ESdChangeReason {
  ChangeDiFriendlyName = 1,
  DiActionUpdated = 2,
  SetButtonOfRunOnEnter = 3,
  ChangeDiAlignment = 4,
  LoadProject = 5,
  UpdateAuth = 6,
  UpdateLang = 7,
  DragDropDi = 8,
  ChangeResponsiveRowColumnSettings = 9,
  ChangePropertyType = 10,
  UpdateValidation = 11,
  UpdateVisibilty = 12,
  ChangePropertyValue = 13,
  ChangeSelectedDiByTools = 14,
  ChangeSelectedDiByCanvasFrame = 15,
  OtherSetStates = 16,
  Undo = 17,
  Redo = 18,
  Terminate = 19,
  Loading = 20,
  ExpandedProperties = 21,
  ChangeStyleValue = 22,
  ChangedCustomFieldOnPropertyPanel = 23,
  ScreenReload = 24,
  SaveWorkflow = 25,
  ClickWfDesignXmlNode = 26,
  PropertyFixedValueChanged = 27,
  StartingScreenIdChanged = 28,
  ConfirmationMessageChanged = 29,
  ActionParameterFixedValueChanged = 30,
  ChangeScreenInput = 34,
  ChangeScreenOutput = 35,
  ActionDescriptionChanged = 36,
  WorkflowListUpdate = 37,
  WFStepMappingChange = 38,
  RecurringIntervalChange = 39,
  UpdateEditability = 40,
  UpdateShortcuts = 41,
  DragDropBlock = 42,
  ChangeProjectStyle = 43,
  DesignTimeElementStateChanged = 44,
  ComponentsMigrated = 45,
  /** A granular remote collaboration op was applied to the local tree (collab P2). */
  RemoteCollabOp = 46,
  /** The whole tree was replaced by the collab session's authoritative snapshot (P1 live view / resync). */
  RemoteResync = 47,
  /** A granular remote workflow collab op was applied to the live BPMN canvas / selectedWf (Faz 4b). */
  RemoteWorkflowCollabOp = 48,
  /** The workflow was replaced by the collab session's authoritative envelope (full resync fallback). */
  RemoteWorkflowResync = 49,
  /**
   * A component's styleObj was written by something other than the styling panel (the canvas selection
   * bar's quick styles and style-tag chip). Same effect as ChangeStyleValue, plus an open styling panel
   * reloads its form so its own pending submit cannot overwrite the write.
   */
  ChangeStyleValueExternal = 50
}

export interface IReportParameter {
  name: string;
  symbol: ISymbol;
}

export interface IScreenDesignPublicJSON {
  screenInputs?: IScreenInput[];
  screenOutputs?: IScreenOutput[];
  screenType?: EScreenType;
  subScreenType?: ESubScreenType;
  masterId?: string;
  supportOffline?: boolean;
  shouldRefresh?: boolean;
  dataCacheEnabled?: boolean;
  reportParameters?: IReportParameter[];
}

export interface IScreenDesignInfo {
  id: string;
  name: string;
  publicMetaData: IScreenDesignPublicJSON;
}

export interface ILanguage {
  id: string;
  name: string;
  shortName: string;
  isDefault: boolean;
  languagePhrases: ILanguagePhrase[];
}

export interface ILanguagePhrase {
  kuikaId: number;
  propertyName: string;
  value: string;
  tagName: string;
  currentFixedValue?: string;
  isConfirmation?: boolean;
  isConfirmationAction?: boolean;
  isNotify?: boolean;
  isProjectTranslation?: boolean;
}

export interface ITranslationByScreenModel {
  screenName: string;
  screenId: string;
  languagePhrases: ILanguagePhrase[];
}

export interface ICollectAllTranslatablePhrasesResponse {
  translationByScreen: ITranslationByScreenModel[];
}
